import type { AdvisorOptions } from '../host/types';
import { ADVISOR_VERSION } from '../version';

export const DEFAULT_FEEDBACK_FORM_ENDPOINT = 'https://formspree.io/f/mqpaqywa';
export const DEFAULT_NAME_SUGGESTION_FORM_ENDPOINT = 'https://formspree.io/f/xnpndgok';
export const TURNSTILE_RESPONSE_FIELD = 'cf-turnstile-response';

export type BetaFeedbackConfiguration = {
  enabled: boolean;
  feedbackFormEndpoint: string;
  nameSuggestionFormEndpoint: string;
};

export type BetaTechnicalMetadata = {
  submissionType: 'NameSuggestion' | 'Feedback';
  subject: 'New ASA name suggestion' | 'New ASA feedback';
  constructorThreadId: string;
  constructorRequestUrl: string;
  pageUrl: string;
  submittedAt: string;
  userAgent: string;
  agentName: string;
  appVersion: string;
};

function isHttpsUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Resolves all beta form settings in one place so the feature can be removed
 * without touching the shopping-agent conversation implementation.
 */
export function getBetaFeedbackConfiguration(options: AdvisorOptions): BetaFeedbackConfiguration {
  return {
    enabled: options.config?.EnableBetaFeedbackForms ?? true,
    feedbackFormEndpoint: isHttpsUrl(options.config?.FeedbackFormEndpoint)
      ? options.config.FeedbackFormEndpoint
      : DEFAULT_FEEDBACK_FORM_ENDPOINT,
    nameSuggestionFormEndpoint: isHttpsUrl(options.config?.NameSuggestionFormEndpoint)
      ? options.config.NameSuggestionFormEndpoint
      : DEFAULT_NAME_SUGGESTION_FORM_ENDPOINT,
  };
}

/**
 * Rebuilds the Constructor diagnostic URL from a fixed, safe origin and path.
 * It intentionally never reads an SDK/network URL, so keys, tokens, prompts,
 * result data, or other future query fields cannot enter a form submission.
 */
export function buildSafeConstructorDiagnosticUrl(threadId: string): string {
  const safeThreadId = threadId.trim();
  if (!safeThreadId) return '';

  const diagnosticUrl = new URL('/v1/intent', 'https://agent.cnstrc.com');
  diagnosticUrl.searchParams.set('thread_id', safeThreadId);
  return diagnosticUrl.toString();
}

export function buildTechnicalMetadata({
  submissionType,
  threadId,
  agentName,
}: {
  submissionType: BetaTechnicalMetadata['submissionType'];
  threadId: string | undefined;
  agentName: string;
}): BetaTechnicalMetadata {
  const constructorThreadId = typeof threadId === 'string' ? threadId.trim() : '';

  return {
    submissionType,
    subject: submissionType === 'NameSuggestion' ? 'New ASA name suggestion' : 'New ASA feedback',
    constructorThreadId,
    constructorRequestUrl: buildSafeConstructorDiagnosticUrl(constructorThreadId),
    pageUrl: window.location.href,
    submittedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    agentName,
    appVersion: ADVISOR_VERSION,
  };
}

/**
 * Posts directly to Formspree. Content-Type is deliberately omitted so the
 * browser supplies the multipart boundary for FormData.
 */
export async function submitBetaFeedbackForm({
  endpoint,
  values,
  metadata,
  turnstileToken,
}: {
  endpoint: string;
  values: Record<string, string>;
  metadata: BetaTechnicalMetadata;
  turnstileToken?: string;
}): Promise<void> {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => formData.append(key, value));
  Object.entries(metadata).forEach(([key, value]) => formData.append(key, value));

  // This is intentionally key-free. A future Turnstile widget can supply the
  // token through this already-supported field without changing submission code.
  if (turnstileToken) formData.append(TURNSTILE_RESPONSE_FIELD, turnstileToken);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  });

  if (!response.ok) throw new Error('Formspree submission failed');
}
